import React, { useState, useEffect, useRef } from 'react';
import { Tag, Space, Button, message, Modal, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, DeleteOutlined, ClockCircleOutlined, LoginOutlined, ReloadOutlined } from '@ant-design/icons';
import { Account } from '../../types/entity/account';
import { accountApi } from '../../services/manager/account';
import List, { ListRef } from '../../components/Templates/List';
import type { FieldConfig } from '../../components/ForgeQueryBuilder';

const { Text } = Typography;

const STATUS_OPTIONS = [
  { label: '活跃', value: 'active' },
  { label: '非活跃', value: 'inactive' },
  { label: '暂停', value: 'suspended' },
  { label: '暂停使用', value: 'block' },
  { label: '已删除', value: 'deleted' },
];

const Accounts: React.FC = () => {
  const listRef = useRef<ListRef>(null);
  const [, forceUpdate] = useState({});
  const isLocalEnv = (process.env.REACT_APP_ENV === 'local') ||
    (process.env.NODE_ENV !== 'production' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));

  // 获取账户状态显示文本
  const getAccountStatusText = (status: string) => {
    const statusMap: { [key: string]: { text: string; color: string } } = {
      'active': { text: '活跃', color: 'green' },
      'inactive': { text: '非活跃', color: 'orange' },
      'suspended': { text: '暂停', color: 'red' },
      'block': { text: '暂停使用', color: 'red' },
      'deleted': { text: '已删除', color: 'default' },
    };
    return statusMap[status] || { text: '未知', color: 'default' };
  };

  // 获取账户类型显示文本
  const getAccountTypeText = (type: string) => {
    const typeMap: { [key: string]: { text: string; color: string } } = {
      'rrzu': { text: 'RRZU', color: 'blue' },
      'admin': { text: '管理员', color: 'purple' },
      'user': { text: '用户', color: 'cyan' },
      'guest': { text: '访客', color: 'default' },
    };
    return typeMap[type] || { text: '未知', color: 'default' };
  };

  // 格式化时间
  const formatTime = (timeStr: string) => {
    return new Date(timeStr).toLocaleString('zh-CN');
  };

  // 格式化过期时间
  const formatExpTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('zh-CN');
  };

  // 检查是否过期
  const isExpired = (expTime: number) => {
    return Date.now() > expTime;
  };

  // 格式化剩余时间
  const formatRemainingTime = (expTime: number) => {
    const now = Date.now();
    const remaining = expTime - now;
    
    if (remaining <= 0) {
      return { text: '已过期', color: '#ff4d4f', isExpired: true };
    }

    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    
    let text = '';
    let color = '#52c41a'; // 绿色
    
    if (days > 0) {
      text = `${days}天${hours}时`;
      if (days <= 3) color = '#faad14'; // 黄色
      if (days <= 1) color = '#ff7a45'; // 橙色
    } else if (hours > 0) {
      text = `${hours}时${minutes}分`;
      color = '#ff7a45'; // 橙色
    } else {
      text = `${minutes}分钟`;
      color = '#ff4d4f'; // 红色
    }
    
    return { text, color, isExpired: false };
  };

  const handleEdit = (record: Account) => {
    message.info(`编辑账户: ${record.id}`);
  };

  const handleDelete = (record: Account) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除账户 ${record.id} 吗？`,
      onOk() {
        message.success('删除成功');
      },
    });
  };

  const handleSimulateLogin = async (record: Account) => {
    try {
      if (record.type !== 'rrzu') {
        message.warning('仅支持 RRZU 账户模拟登陆');
        return;
      }
      const cookieObj = record.cookie as Record<string, string> | undefined;
      if (!cookieObj || Object.keys(cookieObj).length === 0) {
        message.warning('该账户没有可用的 Cookie');
        return;
      }

      const jsScript = `(() => {\n  const cookieMap = ${JSON.stringify(cookieObj)};\n  const domain = '.rrzu.com';\n  const attrs = ['domain=' + domain, 'path=/', 'Secure', 'SameSite=Lax'];\n  Object.entries(cookieMap).forEach(([name, value]) => {\n    document.cookie = name + '=' + encodeURIComponent(value) + '; ' + attrs.join('; ');\n    console.log('Set cookie:', name);\n  });\n  console.log('Done. domain=', domain);\n})();`;

      try {
        await navigator.clipboard.writeText(jsScript);
        message.success('已复制 Cookie 设置脚本到剪贴板，前往 rrzu 控制台粘贴执行');
      } catch (e) {
        // ignore clipboard errors
      }

      window.open('https://admin.rrzu.com/', '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('模拟登陆失败:', err);
      message.error('模拟登陆失败');
    }
  };

  const columns: ColumnsType<Account> = [
    {
      title: '账户ID',
      dataIndex: 'id',
      key: 'id',
      width: 180,
      fixed: 'left',
      render: (text: string) => (
        <Text copyable style={{ fontSize: '12px' }}>
          {text ? `${text.slice(0, 8)}...${text.slice(-8)}` : '-'}
        </Text>
      ),
    },
    {
      title: '公司名',
      key: 'company',
      width: 100,
      render: (item: Account) => item.serverInfo?.company || '-',
    },
    {
      title: '账户类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => {
        const { text, color } = getAccountTypeText(type);
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const { text, color } = getAccountStatusText(status);
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '服务器ID',
      dataIndex: 'serverId',
      key: 'serverId',
      width: 100,
    },
    {
      title: '过期时间',
      dataIndex: 'expTime',
      key: 'expTime',
      width: 180,
      render: (expTime: number) => (
        <div>
          <div style={{ 
            color: isExpired(expTime) ? '#ff4d4f' : '#52c41a' 
          }}>
            {formatExpTime(expTime)}
          </div>
          {isExpired(expTime) && (
            <Tag color="red" style={{ fontSize: '12px' }}>已过期</Tag>
          )}
        </div>
      ),
    },
    {
      title: '过期倒计时',
      dataIndex: 'expTime',
      key: 'countdown',
      width: 150,
      render: (expTime: number) => {
        const { text, color, isExpired: expired } = formatRemainingTime(expTime);
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ClockCircleOutlined style={{ color, marginRight: 4 }} />
            <span style={{ color, fontWeight: 500 }}>
              {text}
            </span>
          </div>
        );
      },
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (time: string) => formatTime(time),
    },
    {
      title: '认证令牌',
      dataIndex: 'auth',
      key: 'auth',
      width: 180,
      render: (auth: string) => (
        <Text 
          copyable 
          style={{ fontSize: '12px', maxWidth: '140px' }}
          ellipsis={{ tooltip: auth }}
        >
          {auth ? `${auth.slice(0, 20)}...` : '-'}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {isLocalEnv && (
            <Button
              type="link"
              icon={<LoginOutlined />}
              size="small"
              onClick={() => handleSimulateLogin(record)}
            >
              模拟登陆
            </Button>
          )}
          <Button 
            type="link" 
            icon={<DeleteOutlined />} 
            size="small"
            danger
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const fetcher = async (params: any) => {
    const filter: any = {};
    if (params.keyword) {
      filter.keyword = params.keyword;
    }
    if (params.status) {
      filter.status = params.status;
    }

    const res = await accountApi.list({
      page: params.page || 1,
      pageSize: params.pageSize || 20,
      filter: Object.keys(filter).length > 0 ? filter : undefined
    });

    return {
      rows: res.data.items || [],
      pagination: res.data.pagination
    };
  };

  const queryFields: FieldConfig[] = [
    { key: 'keyword', label: '关键词', type: 'string', placeholder: 'ID/公司名' },
    { key: 'status', label: '状态', type: 'select', options: STATUS_OPTIONS, },
  ];

  useEffect(() => {
    // 每分钟更新一次倒计时
    const timer = setInterval(() => {
      forceUpdate({});
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <List<Account>
        ref={listRef}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>账户管理</span>
            <Button icon={<ReloadOutlined />} onClick={() => listRef.current?.reload()}>刷新</Button>
          </div>
        }
        queryFields={queryFields}
        fetcher={fetcher}
        buildParams={(q) => {
          const params: any = {
            page: q.page || 1,
            pageSize: q.pageSize || 20
          };
          if (q.filter) {
            const filter = q.filter;
            Object.keys(filter).forEach(key => {
              const filterItem = filter[key];
              if (filterItem && typeof filterItem === 'object' && 'value' in filterItem) {
                params[key] = filterItem.value;
              }
            });
          }
          return params;
        }}
        columns={columns}
        rowKey={(r) => r.id}
        tableProps={{ scroll: { x: 'max-content' } }}
      />
    </>
  );
};

export default Accounts;
